import { Fragment, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Command } from "~/components/guides/components";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  BucketOutputType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { getError } from "~/lib/handle-error";
import { IntegrationAddHeader } from "../../headers/IntegrationAddHeader";
import {
  helperTextStyles,
  ValidationMessage,
} from "../../utils/integrationValidationUtils";
import { AnimatePresence, motion } from "motion/react";
import { Dropzone, DropzoneProps, DropzoneText } from "~/components/dropzone";
import { fadeInOut } from "~/lib/animations";
import useGenerateIntegrationName from "../../utils/integrationsUtils";
import { UpdateFlowData } from "../../utils/integrationsTypes";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";
import { GreenCheckCircleIcon } from "~/components/GreenCheckCircleIcon";

type GcsBucketFormInput = {
  integrationName: string;
  bucket: string;
  output: BucketOutputType;
  serviceAccount: string;
  wifAudience: string;
  wifServiceAccountEmail: string;
};

const defaultValues: GcsBucketFormInput = {
  integrationName: "",
  bucket: "",
  output: BucketOutputType.Jsonl,
  serviceAccount: "",
  wifAudience: "",
  wifServiceAccountEmail: "",
};

export function GcsBucketIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  let navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [serviceAccount, wifAudience] = watch([
    "serviceAccount",
    "wifAudience",
  ]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: {
          input: { mrn: integrationMrn, type: ActionType.RunExport },
        },
      });
    },
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      if (
        updateFlow.integration.configurationOptions?.__typename ===
        "GcsBucketConfigurationOptions"
      ) {
        const { bucket, output, wifAudience, wifServiceAccountEmail } =
          updateFlow.integration.configurationOptions;
        reset({
          integrationName: updateFlow.integration.name,
          bucket: bucket,
          output:
            output === "JSONL" ? BucketOutputType.Jsonl : BucketOutputType.Csv,
          wifAudience: wifAudience ?? "",
          wifServiceAccountEmail: wifServiceAccountEmail ?? "",
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<GcsBucketFormInput> = async (data) => {
    const wifAudience = data.wifAudience?.trim() || undefined;
    const wifServiceAccountEmail =
      data.wifServiceAccountEmail?.trim() || undefined;
    const gcsBucketConfigurationOptions = {
      bucket: data.bucket.trim(),
      output: data.output,
      serviceAccount: data.serviceAccount.trim(),
      wifAudience,
      wifServiceAccountEmail,
    };

    try {
      if (updateFlow) {
        const integrationId = updateFlow.integration.mrn.split("/").pop();
        const mrn = getIntegrationMrn({ scope, integrationId });
        await updateIntegration({
          variables: {
            input: {
              mrn,
              type: ClientIntegrationType.GcsBucket,
              configurationOptions: {
                gcsBucketConfigurationOptions,
              },
            },
          },
        });

        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        navigate(
          `/space/integrations/gcs_bucket/${integrationId}/?${scope.params}`,
        );
      } else {
        await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.GcsBucket,
              longLivedToken: false,
              configurationOptions: {
                gcsBucketConfigurationOptions,
              },
            },
          },
        });
        navigate(`/space/integrations/gcs_bucket?${scope.params}`);
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const onDropAccepted: DropzoneProps["onDropAccepted"] = async (files) => {
    setValue("serviceAccount", await files[0].text());
  };

  setDocumentTitle(["GCP Cloud Storage Bucket", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader {...{ type: IntegrationType.GcsBucket }} />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Step 1 */}
          <Box pb={4}>
            <Command
              number={1}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Choose an integration name
            </Command>

            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Please choose a descriptive name that lets you easily identify
                your integration.
              </Typography>
              <Controller
                name="integrationName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    disabled={updateFlow ? true : false}
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="Your integration name..."
                    error={Boolean(errors.integrationName)}
                    helperText={
                      Boolean(errors.integrationName) && (
                        <ValidationMessage error={errors.integrationName} />
                      )
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* Step 2 */}
          <Box pb={4}>
            <Command
              number={2}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Define your export settings
            </Command>

            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Configure Mondoo data exports to Google Cloud Storage. To learn
                more, read the{" "}
                <Link
                  href="https://mondoo.com/docs/reporting/export/gcs-bucket"
                  target="_blank"
                  rel="noopener"
                >
                  Mondoo documentation
                </Link>
                .
              </Typography>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Bucket name
              </Typography>
              <Controller
                name="bucket"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      mb: 3,
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="Name of target bucket ..."
                    error={Boolean(errors.bucket)}
                    helperText={
                      getFieldState("bucket").isTouched &&
                      Boolean(errors.bucket) && (
                        <ValidationMessage
                          error={errors.bucket}
                          integrationTypeId="gcs_bucket"
                        />
                      )
                    }
                  />
                )}
              />

              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Export as
              </Typography>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="filetype-select-label">
                  Select a filetype...
                </InputLabel>
                <Controller
                  name="output"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Fragment>
                      <Select
                        labelId="filetype-select-label"
                        id="filetype-select"
                        label="filetype"
                        error={Boolean(errors.output)}
                        sx={{
                          "& .MuiInputBase-input": {
                            background: theme.palette.code.background,
                          },
                          "& .MuiSelect-icon": {
                            color: "text.secondary",
                          },
                        }}
                        {...field}
                      >
                        <MenuItem value={BucketOutputType.Jsonl}>
                          .jsonl
                        </MenuItem>
                        <MenuItem value={BucketOutputType.Csv}>.csv</MenuItem>
                      </Select>
                      {getFieldState("output").isTouched &&
                        Boolean(errors.output) && (
                          <FormHelperText>
                            <ValidationMessage
                              error={errors.output}
                              integrationTypeId="gcs_bucket"
                            />
                          </FormHelperText>
                        )}
                    </Fragment>
                  )}
                />
              </FormControl>
            </Box>
          </Box>

          {/* Step 3 - Authentication */}
          <Box pb={4}>
            <Command
              number={3}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Configure authentication
            </Command>

            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Choose one of the following authentication methods:
              </Typography>

              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Option A: Workload Identity Federation (recommended)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use keyless authentication with Workload Identity Federation. To
                learn more, read the{" "}
                <Link
                  href="https://cloud.google.com/iam/docs/workload-identity-federation"
                  target="_blank"
                  rel="noopener"
                >
                  Google documentation
                </Link>
                .
              </Typography>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                WIF Audience URL
              </Typography>
              <Controller
                name="wifAudience"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      mb: 3,
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="https://iam.googleapis.com/projects/PROJECT_NUMBER/locations/global/workloadIdentityPools/POOL_ID/providers/PROVIDER_ID"
                  />
                )}
              />
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Service Account Email (for impersonation)
              </Typography>
              <Controller
                name="wifServiceAccountEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="service-account@project.iam.gserviceaccount.com"
                  />
                )}
              />

              <Typography
                fontWeight={700}
                sx={{ mb: 1, mt: 4, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    height: "1px",
                    bgcolor: "divider",
                    mr: 2,
                  }}
                />
                OR
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    height: "1px",
                    bgcolor: "divider",
                    ml: 2,
                  }}
                />
              </Typography>

              <Typography fontWeight={700} sx={{ mb: 1, mt: 3 }}>
                Option B: Service Account JSON Key
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a service account JSON key file. To learn more, read the{" "}
                <Link
                  href="https://mondoo.com/docs/reporting/export/gcs-bucket"
                  target="_blank"
                  rel="noopener"
                >
                  Mondoo documentation
                </Link>{" "}
                and the{" "}
                <Link
                  href="https://docs.cloud.google.com/iam/docs/service-accounts-create"
                  target="_blank"
                  rel="noopener"
                >
                  Google documentation
                </Link>
                .
              </Typography>
              <AnimatePresence mode="wait">
                {!serviceAccount ? (
                  <Box key="dropzone" component={motion.div} {...fadeInOut}>
                    <Dropzone
                      className="mondoo-dropzone-area"
                      accept={{ "application/json": [] }}
                      multiple={false}
                      showAlerts={["error"]}
                      onDropAccepted={onDropAccepted}
                    >
                      <DropzoneText>
                        Drag and drop your .json file here.
                      </DropzoneText>
                    </Dropzone>
                  </Box>
                ) : (
                  <Box
                    key="success"
                    component={motion.div}
                    {...fadeInOut}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">
                        Successfully loaded JSON file
                      </Typography>
                      <GreenCheckCircleIcon fontSize="large" sx={{ ml: 2 }} />
                    </Box>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setValue("serviceAccount", "")}
                    >
                      Remove JSON file
                    </Button>
                  </Box>
                )}
              </AnimatePresence>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              endIcon={<ChevronRightIcon />}
              disabled={!isValid || (!serviceAccount && !wifAudience)}
            >
              {updateFlow ? "update configuration" : "create export"}
            </Button>
          </Box>
        </form>
      </Box>
    </Fragment>
  );
}
